import React, { PropsWithChildren, useCallback } from "react";

import { AmplitudeApi } from "@/api/amplitude";
import { useAuthContext } from "@/context/auth-renderer/Auth.context";
import { useSearchParamsContextV2 } from "@/shared/search-params-context";
import {
  ObjectKeys,
  deepStringifyObjectValues,
  removeKeysWithNullableValues,
} from "@/utils/object.utils";

import { convertFormValuesToParams } from "../helpers";
import { convertUrlParamsToFormValues } from "../helpers";
import { useSearchLadies } from "../hooks";
import { SearchInputsSchema, SearchPageUrlSearchParams } from "../models";

const SearchPageContext = React.createContext<ReturnType<
  typeof useCreateSearchPageContextValue
> | null>(null);

const isRawSSRSearchParamsExists = (
  rawSSRUrlSearchParams: URLSearchParams | null,
): rawSSRUrlSearchParams is NonNullable<URLSearchParams> => {
  return (
    Boolean(rawSSRUrlSearchParams) &&
    Array.from(rawSSRUrlSearchParams?.entries() || []).length > 0
  );
};

const useCreateSearchPageContextValue = () => {
  const pageSize = 12;
  const { urlSearchParams, setUrlSearchParams, rawSSRUrlSearchParams } =
    useSearchParamsContextV2(SearchPageUrlSearchParams);

  const formValues = convertUrlParamsToFormValues(
    isRawSSRSearchParamsExists(rawSSRUrlSearchParams)
      ? rawSSRUrlSearchParams
      : new URLSearchParams(
          deepStringifyObjectValues(
            removeKeysWithNullableValues(
              (urlSearchParams as Required<typeof urlSearchParams>) || {},
            ),
          ),
        ),
  );

  const page = urlSearchParams?.page || 1;
  const {
    data,
    clientMutations: { toggleLadyFavorite },
  } = useSearchLadies({
    pageSize,
    startIndex: page * pageSize - pageSize,
    urlSearchParams,
  });
  const pageCount = data?.count ? Math.ceil(data.count / pageSize) : 0;

  const { isAuthenticated } = useAuthContext();

  const submitToSearchParamsContext = useCallback(
    (formValues: SearchInputsSchema) => {
      const newSearchParams = convertFormValuesToParams(formValues);

      if (
        newSearchParams &&
        !!ObjectKeys(newSearchParams).length &&
        isAuthenticated
      ) {
        AmplitudeApi.send({
          amplitude: "search",
          search_field: ObjectKeys(newSearchParams),
        });
      }

      setUrlSearchParams(newSearchParams || {});
    },
    [isAuthenticated, setUrlSearchParams],
  );

  const mixPageToParams = (page: number) => {
    setUrlSearchParams({ page }, { append: true });
  };

  return {
    toggleLadyFavorite,
    data,
    submitToSearchParamsContext,
    mixPageToParams,
    formValues,
    pageCount,
  };
};

export const SearchPageContextProvider = ({ children }: PropsWithChildren) => {
  const contextValue = useCreateSearchPageContextValue();

  return (
    <SearchPageContext.Provider value={contextValue}>
      {children}
    </SearchPageContext.Provider>
  );
};

export const useSearchPageContext = () => {
  const context = React.useContext(SearchPageContext);

  if (!context) {
    throw new Error("use useSearchPageContext with SearchPageContextProvider");
  }

  return context;
};
